/**
 * WeeklyHeatmap — Phase 20.16
 * ------------------------------------------------------------------
 * 7-day horizontal contribution grid tracking daily habit completions
 * for the CURRENT week (Mon → Sun). Coloring uses a DYNAMIC RELATIVE
 * system — each week's brightest cell is anchored to the user's own
 * peak day, so a rough week still has visible progression instead of
 * a wall of empty gray cells.
 *
 *   Tier 0 (0% of max)     → dark translucent gray (empty)
 *   Tier 1 (1-33%)         → muted cyan glow
 *   Tier 2 (34-66%)        → medium cyan
 *   Tier 3 (67-100%)       → bright glowing cyan (with shadow)
 *
 * Data source: `api.getUserWeek(userId)` — single aggregate call.
 */
import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

const TIER_CLASS = [
  // Tier 0 — empty
  "bg-[var(--stakes-surface-2)] border-[var(--stakes-border)] text-[var(--stakes-muted)]",
  // Tier 1 — muted glow
  "bg-[color:rgb(56_189_248_/_0.15)] border-[color:rgb(56_189_248_/_0.30)] text-[var(--stakes-child)]",
  // Tier 2 — medium
  "bg-[color:rgb(56_189_248_/_0.40)] border-[color:rgb(56_189_248_/_0.60)] text-white",
  // Tier 3 — brightest / glowing
  "bg-[var(--stakes-child)] border-[var(--stakes-child)] text-black shadow-[0_0_14px_var(--stakes-child)]",
];

function tierFor(points, maxDayPoints) {
  if (!points || points <= 0) return 0;
  if (!maxDayPoints || maxDayPoints <= 0) return 0;
  const pct = points / maxDayPoints;
  if (pct <= 0.33) return 1;
  if (pct <= 0.66) return 2;
  return 3;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function formatRange(start, end) {
  if (!start || !end) return "";
  try {
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "2-digit" }).toUpperCase();
    return `${fmt(s)} → ${fmt(e)}`;
  } catch {
    return "";
  }
}

export default function WeeklyHeatmap({ userId, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getUserWeek(userId);
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || "Failed to load week");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // refreshKey lets the parent re-trigger a fetch after check-in writes
    // without having to lift the whole heatmap state up.
  }, [userId, refreshKey]);

  const today = useMemo(todayISO, []);
  const maxDayPoints = data?.max_day_points ?? 0;

  return (
    <section
      className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)] p-4 md:p-5"
      data-testid="weekly-heatmap"
    >
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="label-mono">THIS WEEK</div>
          <h3 className="font-anton text-2xl md:text-3xl uppercase mt-1 leading-none">
            7-DAY HEAT
          </h3>
        </div>
        <div className="label-mono text-right flex-shrink-0">
          {data ? formatRange(data.week_start, data.week_end) : "—"}
          {data && maxDayPoints > 0 && (
            <div className="mt-1" style={{ color: "var(--stakes-child)" }}>
              PEAK · +{maxDayPoints}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div
          className="mt-4 label-mono text-center py-6"
          style={{ color: "var(--stakes-primary)" }}
          data-testid="weekly-heatmap-error"
        >
          {String(error).toUpperCase()}
        </div>
      )}

      {!error && !data && (
        <div className="mt-4 label-mono text-center py-6" data-testid="weekly-heatmap-loading">
          LOADING…
        </div>
      )}

      {!error && data && (
        <div
          className="mt-4 grid grid-cols-7 gap-2"
          data-testid="weekly-heatmap-grid"
        >
          {data.days.map((d) => {
            const tier = tierFor(d.points, maxDayPoints);
            const isToday = d.date === today;
            return (
              <div
                key={d.date}
                data-testid={`heatmap-day-${d.date}`}
                data-tier={tier}
                className={`relative aspect-square rounded-md border flex flex-col items-center justify-center p-1 transition-colors ${TIER_CLASS[tier]}`}
                title={`${d.label} · ${d.date} · ${d.points} pts (tier ${tier})`}
              >
                {isToday && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-md pointer-events-none ring-1 ring-white/60"
                  />
                )}
                <div
                  className="label-mono text-[10px] leading-none"
                  data-testid={`heatmap-day-label-${d.date}`}
                >
                  {d.label}
                </div>
                <div className="font-anton text-lg md:text-xl leading-none mt-1 tabular-nums">
                  {d.points > 0 ? `+${d.points}` : d.points < 0 ? d.points : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
